import { useMemo, useState } from "react";
import Modal, { ModalRoot, ModalTrigger } from "../../../../Modal/Modal";
import Button from "@/components/bricks/button/Button";
import { useQuery } from "@tanstack/react-query";
import { get } from "../../../../../api/util";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import Spinner from "../../../../spinner/Spinner";
import CalendarIcon from "../../../../icons/CalendarIcon";

type TemporalStatus = "not-started" | "in-progress" | "completed";
type MonthlyData = {
  active: boolean;
  temporalStatus: TemporalStatus;
};
type YearlyData = {
  year: number;
  months: {
    januar: MonthlyData;
    februar: MonthlyData;
    marts: MonthlyData;
    april: MonthlyData;
    maj: MonthlyData;
    juni: MonthlyData;
    juli: MonthlyData;
    august: MonthlyData;
    september: MonthlyData;
    oktober: MonthlyData;
    november: MonthlyData;
    december: MonthlyData;
  };
};

type PeriodeData = {
  antalMaanederGennemfoert: number;
  yearlyData: YearlyData[];
};
export default function VisPeriodeModal(props: {
  title: string;
  personId: string;
  efteruddannelsesPeriodeId: string;
  description: string;
}) {
  const { description, title, efteruddannelsesPeriodeId, personId } = props;
  const [open, setOpen] = useState(false);
  const { data: efteruddannelser_maaneder, isLoading } = useQuery<string[]>({
    queryKey: [
      "efteruddannelser_maaneder",
      efteruddannelsesPeriodeId,
      personId,
    ],
    queryFn: async function () {
      return get({
        query: `person/${personId}/efteruddannelser/${efteruddannelsesPeriodeId}/maaneder`,
      });
    },
    enabled: open,
  });
  const data = useMemo<PeriodeData>(() => {
    return transformData(efteruddannelser_maaneder);
  }, [efteruddannelser_maaneder]);
  const { yearlyData } = data;
  return (
    <ModalRoot
      controller={{
        open: open,
        setOpen: setOpen,
      }}
    >
      <ModalTrigger asChild>
        <Button color="secondary" onClick={() => {}} size="sm">
          <CalendarIcon width={17} /> Vis periode
        </Button>
      </ModalTrigger>
      <Modal
        title={
          <>
            <CalendarIcon /> {title}
          </>
        }
        description={description}
      >
        <div className="c-modal-container">
          <div className="c-modal-container-inner c-modal-container-inner--lg">
            <h3 className="h5 mb-4">{description}:</h3>
            {isLoading && <Spinner label="Henter måneder..." />}
            {!isLoading && (
              <>
                <p>Gennemførte måneder: {data.antalMaanederGennemfoert}/36</p>
                {yearlyData.length > 0 && (
                  <div className="table-responsive">
                    <table className="table border-top table--layout-fixed">
                      <tbody>
                        {yearlyData.map((yd) => (
                          <tr key={yd.year}>
                            <th
                              scope="row"
                              className="fw-bold"
                              style={{ width: "5rem" }}
                            >
                              {yd.year}
                            </th>

                            {Object.entries(yd.months).map(([month, info]) => {
                              const { active } = info;
                              return (
                                <td key={month} className="px-0 py-1">
                                  <span
                                    className={`d-block text-center py-1 border-end border-white text-truncate px-1 ${
                                      active
                                        ? "bg-green-light"
                                        : "text-decoration-line-through text-muted"
                                    }`}
                                  >
                                    {month.charAt(0).toUpperCase() +
                                      month.slice(1)}
                                    <span className="sr-only">
                                      {": "}
                                      {active ? "Ja" : "Nej"}
                                    </span>
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {yearlyData.length === 0 && (
                  <p className="text-muted">Ingen måneder at vise</p>
                )}
              </>
            )}
          </div>
        </div>
      </Modal>
    </ModalRoot>
  );
}

/**
 *
 * @param year current year
 * @param month current month (0-11)
 * @returns TemporalStatus
 */
function getTemporalStatusForMonth(
  year: number,
  month: number,
): TemporalStatus {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  if (year < currentYear) return "completed";
  if (year === currentYear && month < currentMonth) return "completed";
  if (year === currentYear && month === currentMonth) return "in-progress";
  return "not-started";
}

function transformData(
  efteruddannelser_maaneder: string[] | undefined,
): PeriodeData {
  const data: PeriodeData = {
    antalMaanederGennemfoert: 0,
    yearlyData: [],
  };
  if (!efteruddannelser_maaneder) return data;
  const sortedData = efteruddannelser_maaneder.sort();
  const sortedDates = sortedData.map((d) => new Date(d));
  if (!sortedDates.length) return data;
  const firstYear = sortedDates[0].getFullYear();
  const lastYear = sortedDates[sortedDates.length - 1].getFullYear();
  const numberOfYears = lastYear - firstYear + 1;

  for (let i = 0; i < numberOfYears; i++) {
    const year = firstYear + i;
    const months = {
      januar: {
        active: false,
        temporalStatus: getTemporalStatusForMonth(year, 0),
      },
      februar: {
        active: false,
        temporalStatus: getTemporalStatusForMonth(year, 1),
      },
      marts: {
        active: false,
        temporalStatus: getTemporalStatusForMonth(year, 2),
      },
      april: {
        active: false,
        temporalStatus: getTemporalStatusForMonth(year, 3),
      },
      maj: {
        active: false,
        temporalStatus: getTemporalStatusForMonth(year, 4),
      },
      juni: {
        active: false,
        temporalStatus: getTemporalStatusForMonth(year, 5),
      },
      juli: {
        active: false,
        temporalStatus: getTemporalStatusForMonth(year, 6),
      },
      august: {
        active: false,
        temporalStatus: getTemporalStatusForMonth(year, 7),
      },
      september: {
        active: false,
        temporalStatus: getTemporalStatusForMonth(year, 8),
      },
      oktober: {
        active: false,
        temporalStatus: getTemporalStatusForMonth(year, 9),
      },
      november: {
        active: false,
        temporalStatus: getTemporalStatusForMonth(year, 10),
      },
      december: {
        active: false,
        temporalStatus: getTemporalStatusForMonth(year, 11),
      },
    };

    data.yearlyData.push({
      year,
      months,
    });
  }
  for (const date of sortedDates) {
    const year = date.getFullYear();
    const monthName = format(date, "MMMM", { locale: da }).toLowerCase();
    const monthData = data.yearlyData.find((yd) => yd.year === year);
    if (monthData) {
      monthData.months[monthName].active = true;
      if (
        ["completed", "in-progress"].indexOf(
          monthData.months[monthName].temporalStatus,
        ) > -1
      ) {
        data.antalMaanederGennemfoert++;
      }
    }
  }
  return data;
}
